/**
 * Identity Verification Component
 * Task 2.4: Implement Ghana Card Verification Frontend
 * 
 * User Story: "As a claimant, I must provide my passport photo and Ghana Card number for identity verification 
 * before submitting a claim, with clear feedback if verification fails."
 * 
 * This component handles passport photo upload and Ghana Card number input for identity verification.
 */

import React, { useState, useRef, useEffect } from 'react';
import { uploadIdentityVerification, validatePassportPhoto } from '../verificationService';
import OmTextField from '../../../components/ui/OmTextField';
import OmDocumentUpload from '../../../components/ui/OmDocumentUpload';
import OmButton from '../../../components/ui/OmButton';

const GhanaCardUpload = ({ 
  onVerificationId, 
  onError, 
  onStageChange,
  disabled = false,
  className = '' 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [ghanaCardNumber, setGhanaCardNumber] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const documentUploadRef = useRef(null);

  /**
   * Handle file selection from Om Document Upload
   */
  const handleFileSelect = (file, event) => {
    console.log('🟡 GhanaCardUpload: handleFileSelect called', {
      file,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      event,
      eventType: event?.type
    });

    if (!file) {
      console.log('🟠 GhanaCardUpload: No file provided, resetting selection');
      resetFileSelection();
      return;
    }

    try {
      // Validate the selected file
      console.log('🔍 GhanaCardUpload: Validating file', { file: file.name, size: file.size, type: file.type });
      validatePassportPhoto(file);
      
      // Additional client-side validation for PNG support
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Please upload a PNG, JPEG, JPG, or WebP image file');
      }
      
      console.log('✅ GhanaCardUpload: File validation passed, setting selected file');
      setSelectedFile(file);
      
      // Generate preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      // Clear any previous errors
      setValidationErrors(prev => ({ ...prev, image: null }));
      if (onError) {
        onError(null);
      }
    } catch (error) {
      console.error('File validation failed:', error);
      resetFileSelection();
      setValidationErrors(prev => ({ ...prev, image: error.message }));
      if (onError) {
        onError(error.message);
      }
    }
  };

  /**
   * Handle Ghana Card number change from Om Text Field
   */
  const handleGhanaCardNumberChange = (e) => {
    const value = e.target.value;
    setGhanaCardNumber(value);
    // Clear validation error when user starts typing
    if (validationErrors.ghanaCardNumber) {
      setValidationErrors(prev => ({ ...prev, ghanaCardNumber: null }));
    }
  };

  /**
   * Reset file selection and cleanup preview URL
   */
  const resetFileSelection = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  /**
   * Handle file upload errors from Om component
   */
  const handleFileError = (error, event) => {
    let errorMessage = 'File upload error';
    
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && error.message) {
      errorMessage = error.message;
    } else if (error && error.error) {
      errorMessage = error.error;
    }
    
    setValidationErrors(prev => ({ ...prev, image: errorMessage }));
    if (onError) {
      onError(errorMessage);
    }
    resetFileSelection();
  };

  /**
   * Handle identity verification upload process
   */
  const handleUpload = async () => {
    console.log('🚀 GhanaCardUpload: handleUpload called', {
      hasSelectedFile: !!selectedFile,
      fileName: selectedFile?.name,
      ghanaCardNumber: ghanaCardNumber,
      ghanaCardNumberLength: ghanaCardNumber.length
    });

    if (!selectedFile) {
      console.log('❌ GhanaCardUpload: No file selected');
      if (onError) {
        onError('Please select a passport photo first.');
      }
      return;
    }

    // Validate Ghana Card number format
    const ghanaCardPattern = /^GHA-\d{9}-\d$/;
    if (!ghanaCardNumber.trim()) {
      setValidationErrors(prev => ({ ...prev, ghanaCardNumber: 'Please enter your Ghana Card number.' }));
      if (onError) {
        onError('Please enter your Ghana Card number.');
      }
      return;
    }
    
    if (!ghanaCardPattern.test(ghanaCardNumber.trim())) {
      console.log('❌ GhanaCardUpload: Invalid Ghana Card format', { ghanaCardNumber: ghanaCardNumber.trim() });
      setValidationErrors(prev => ({ ...prev, ghanaCardNumber: 'Please enter a valid Ghana Card number (format: GHA-XXXXXXXXX-X).' }));
      if (onError) {
        onError('Please enter a valid Ghana Card number (format: GHA-XXXXXXXXX-X).');
      }
      return;
    }

    console.log('🚀 GhanaCardUpload: Starting upload process', {
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      ghanaCardNumber: ghanaCardNumber.trim()
    });
    setIsUploading(true);

    // Notify parent about stage change to uploading
    if (onStageChange) {
      onStageChange('UPLOADING');
    }

    try {
      // Upload passport photo and Ghana Card number for verification
      console.log('📤 GhanaCardUpload: Calling uploadIdentityVerification');
      
      // Notify parent about stage change to verifying
      if (onStageChange) {
        onStageChange('VERIFYING');
      }
      
      const result = await uploadIdentityVerification(selectedFile, ghanaCardNumber.trim());
      
      console.log('✅ GhanaCardUpload: Upload successful', { result });
      // Emit verification ID to parent component for status polling
      if (onVerificationId && result.verificationId) {
        console.log('📋 GhanaCardUpload: Emitting verification ID', { verificationId: result.verificationId });
        onVerificationId(result.verificationId);
      } else {
        console.log('⚠️ GhanaCardUpload: No verification ID in result or no callback', { 
          hasVerificationId: !!result.verificationId,
          hasCallback: !!onVerificationId 
        });
      }
    } catch (error) {
      // Notify parent about stage change back to input on error
      if (onStageChange) {
        onStageChange('INPUT');
      }
      
      // Phase 2: Enhanced error handling using structured error data
      let userMessage = 'Failed to verify identity. Please try again.';
      
      // Check if we have structured error data with details
      if (error.details) {
        // Use the detailed error message from the backend (e.g., NIA-specific errors)
        userMessage = error.details;
      } else if (error.code) {
        // Use error code-based messages
        switch (error.code) {
          case 'NIA_VERIFICATION_FAILED':
            userMessage = error.reason || error.message || 'Ghana Card verification failed. Please check your details and try again.';
            break;
          case 'INVALID_GHANA_CARD_FORMAT':
            userMessage = 'Invalid Ghana Card format. Please check your Ghana Card number and try again.';
            break;
          case 'INVALID_IMAGE_FORMAT':
            userMessage = 'Invalid image format. Please upload a JPEG or PNG file.';
            break;
          case 'IMAGE_FILE_TOO_LARGE':
            userMessage = 'Image file is too large. Please use a smaller image (under 5MB).';
            break;
          case 'NETWORK_ERROR':
            userMessage = 'Network connection issue. Please check your internet and try again.';
            break;
          case 'INTERNAL_SERVER_ERROR':
            userMessage = 'Server error occurred. Our team has been notified. Please try again in a few minutes.';
            break;
          default:
            userMessage = error.message || 'Failed to verify identity. Please try again.';
        }
      } else {
        // Fallback to legacy string matching for unstructured errors
        if (error.message.includes('Status: 500')) {
          userMessage = 'Server error occurred. Our team has been notified. Please try again in a few minutes.';
        } else if (error.message.includes('network') || error.message.includes('timeout')) {
          userMessage = 'Network connection issue. Please check your internet and try again.';
        } else if (error.message.includes('Ghana Card')) {
          userMessage = 'Invalid Ghana Card format. Please check your Ghana Card number and try again.';
        } else if (error.message.includes('too large')) {
          userMessage = 'Image file is too large. Please use a smaller image (under 5MB).';
        } else if (error.message.includes('invalid') && error.message.includes('image')) {
          userMessage = 'Invalid image format. Please upload a JPEG or PNG file.';
        } else if (error.message.includes('network') || !navigator.onLine) {
          userMessage = 'No internet connection. Please check your network and try again.';
        }
      }
      
      if (onError) {
        onError(userMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`ghana-card-upload ${className}`} style={{ borderRadius: '16px', background: '#fff', border: '1px solid #e5e7eb', padding: '24px' }}>
      {/* Ghana Card Number Input */}
      <div className="mb-6">
        <OmTextField
          label="Ghana Card Number *"
          type="text"
          value={ghanaCardNumber}
          onChange={handleGhanaCardNumberChange}
          disabled={disabled}
          placeholder="GHA-XXXXXXXXX-X"
          required
          error={validationErrors.ghanaCardNumber}
          helperText="Enter your Ghana Card number in the format: GHA-XXXXXXXXX-X"
        />
      </div>

      {/* Upload Area */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Passport Photo *
        </label>
        <OmDocumentUpload
          ref={documentUploadRef}
          placeholder="Passport Photo"
          secondaryPlaceholder="Passport photo (PNG, JPG, JPEG, WebP up to 5MB)"
          acceptedFileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
          maxFileSizeInBytes={5242880} // 5MB
          customErrorMessage={validationErrors.image || ""}
          disabled={disabled}
          onFileSelect={handleFileSelect}
          onError={handleFileError}
          removeFileOnError={resetFileSelection}
          deleteFile={resetFileSelection}
          key="passport-photo-upload"
        />
      </div>

      {/* Image Preview */}
      {previewUrl && (
        <div className="mb-6 text-center">
          <label className="block text-sm font-semibold text-deep-jungle mb-2">Preview</label>
          <div className="max-w-xs mx-auto">
            <img
              src={previewUrl}
              alt="Passport Photo Preview"
              className="w-full h-auto border border-gray-300 shadow-sm"
              style={{ borderRadius: '12px' }}
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {selectedFile && (
        <div className="flex gap-3 justify-center">
          <div 
            style={{
              '--omk-outlined-button-label-text-color': 'var(--color-steel)',
              '--omk-outlined-button-icon-color': 'var(--color-steel)',
              '--omk-outlined-button-outline-color': 'var(--color-silver-mist)',
              '--omk-outlined-button-hover-label-text-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-icon-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-state-layer-color': 'var(--color-silver-mist)'
            }}
          >
            <OmButton
              type="button"
              onClick={resetFileSelection}
              disabled={disabled || isUploading}
              buttonType="outlined-button"
              buttonSize="medium"
              leadingIcon="delete"
            >
              Remove
            </OmButton>
          </div>
          
          <OmButton
            type="button"
            onClick={handleUpload}
            disabled={disabled || isUploading || !selectedFile || !ghanaCardNumber.trim()}
            variant="primary"
            size="medium"
            leadingIcon="upload"
          >
            {isUploading ? 'Verifying Identity...' : 'Verify Identity'}
          </OmButton>
        </div>
      )}
    </div>
  );
};

export default GhanaCardUpload;
